import Vue from "vue";

export const surveyStatus = {
  draft: {
    name: "Draft",
    key: "draft",
    detail:
      "While in draft mode users will not be allowed to enter your survey. Further all responses submitted will be flagged as 'test' responses and cleared when the survey is 'Published' or 'Archived'",
  },
  published: {
    name: "Published",
    key: "published",
    detail:
      "Users are allowed to submit responses within the 'Start Date' and 'End Date'.",
  },
  archived: {
    name: "Archived",
    key: "archived",
    detail:
      "Users are not allowed to submit responses and the survey is considered 'Closed'.",
  },
};

export const reportTypes = {
  itemAnalysis: "Item Analysis",
  agreeAnalysis: "Agree Analysis",
};

export const questionTypes = {
  checklist: "checklist",
  table: "table",
  text: "text",
  radio: "radio",
  rankedChoice: "rankedChoice",
  textBlock: "textBlock",
};

export const questionTypeOptions = [
  {
    text: "Checklist",
    value: questionTypes.checklist,
  },
  {
    text: "Table",
    value: questionTypes.table,
  },
  {
    text: "Text Block",
    value: questionTypes.textBlock,
  },
  {
    text: "Text Field",
    value: questionTypes.text,
  },
  {
    text: "Radio List",
    value: questionTypes.radio,
  },
  {
    text: "Ranked Choice",
    value: questionTypes.rankedChoice,
  },
];

export const questionSettings = {
  columnWidth: {
    key: "columnWidth",
    default: "120",
  },
  required: {
    key: "required",
    default: "true",
  },
  private: {
    key: "private",
    default: "false",
  },
  weightedOptions: {
    key: "weightedOptions",
    default: "false",
  },
  slotCount: {
    key: "slotCount",
    default: "2",
  },
  min: {
    key: "min",
    default: "0",
  },
  max: {
    key: "max",
    default: "1",
  },
  minLength: {
    key: "minLength",
    default: "2",
  },
  maxLength: {
    key: "maxLength",
    default: "200",
  },
  isAlert: {
    key: "isAlert",
    default: "false",
  },
  alertType: {
    key: "alertType",
    default: "",
  },
  alertColor: {
    key: "alertColor",
    default: "",
  },
};

export const alertTypes = {
  default: {
    key: "default",
    label: "Default",
    classes: "grey lighten-1 text--black",
    type: null,
  },
  error: {
    key: "error",
    label: "Error",
    classes: "red darken-1 white--text",
  },
  info: {
    key: "info",
    label: "Info",
    classes: "secondary white--text",
    type: "info",
  },
};

export const alertTypeOptions = (() => {
  let results = [];

  for (let key in alertTypes) {
    results.push({
      text: alertTypes[key].label,
      value: alertTypes[key].key,
      type: alertTypes[key],
    });
  }

  return results;
})();

export function getAlertType(key) {
  return alertTypeOptions.find((x) => x.value === key)?.type;
}

export const ruleTypes = {
  requiredIf: "requiredIf",
  optionalIf: "optionalIf",
  hideIf: "hideIf",
  showIf: "showIf",
  stopIf: "stopIf",
};

export const queryBuilderTypes = {
  rule: "query-builder-rule",
  group: "query-builder-group",
};

export const agreeColors = [
  {
    label: "Greater than or equal to 70%",
    floor: 0.7,
    classes: "green lighten-1",
  },
  {
    label: "Greater than or equal to 50%",
    floor: 0.5,
    classes: "secondary darken-1 white--text",
  },
  {
    label: "Greater than or equal to 30%",
    floor: 0.3,
    classes: "orange lighten-1",
  },
  {
    label: "Below 30%",
    floor: 0,
    classes: "red",
  },
];

export const thresholdByYear = [
  {
    surveyId: "2024_NCTWCS",
    minResponses: 5,
    percentageFloor: 0.5,
    usePrincipalReports: false,
  },
  {
    surveyId: "2022_NCTWCS",
    minResponses: 5,
    percentageFloor: 0.4,
    usePrincipalReports: true,
  },
  {
    surveyId: "2020_NCTWCS",
    minResponses: 5,
    percentageFloor: 0.4,
    usePrincipalReports: true,
  },
  {
    surveyId: "2018_NCTWCS",
    minResponses: 5,
    percentageFloor: 0.4,
    usePrincipalReports: true,
  },
];

export const queryBuildLogicalOperators = {
  all: "&&",
  any: "||",
};

export const accessKeyUseTypes = {
  single: "single",
  multiple: "multiple",
};

export const surveyTemplateTypes = {
  standard: "standard",
  custom: "custom",
};

// TODO: MOVE, SHOULDNT BE HERE
export function getQuestionSetting(settings, key) {
  let target = settings?.find((x) => x.key === key);
  if (target) return target;

  if (!questionSettings[key]) throw "Invalid question setting key";

  return {
    key: questionSettings[key].default,
    value: questionSettings[key].default,
  };
}

export function updateSetting(settings, key, value) {
  let index = settings.findIndex((x) => x.key === key);
  let pair = { key, value };

  if (index > -1) Vue.set(settings, index, pair);
  else settings.push(pair);
}

export function showQuestion(question, data) {
  let result = true;
  let rule = null;
  let rules = question.rules;

  try {
    // handle showIf
    rule = getRule(ruleTypes.showIf, rules);
    if (rule) {
      result = evaluateRule(rule.value, data);
    }

    // handle hideIf
    rule = getRule(ruleTypes.hideIf, rules);
    if (rule) {
      result = !evaluateRule(rule.value, data);
    }
  } catch (e) {
    console.log(question);
    throw e;
  }
  return result;
}

export function hideQuestion(question, data) {
  return !showQuestion(question, data);
}

export function requireQuestion(question, data) {
  let result = false;
  let rule = null;
  let rules = question.rules;

  // handle showIf
  rule = getRule(ruleTypes.requiredIf, rules);
  if (rule) {
    result = evaluateRule(rule.value, data);
  }

  // handle hideIf
  rule = getRule(ruleTypes.optionalIf, rules);
  if (rule) {
    result = !evaluateRule(rule.value, data);
  }

  return result;
}

export function optionalQuestion(question, data) {
  return !requireQuestion(question, data);
}

export function roadBlockQuestion(question, data) {
  let result = false;
  let rule = null;
  let rules = question.rules;

  rule = getRule(ruleTypes.stopIf, rules);
  
  if (rule) {
    result = evaluateRule(rule.value, data);
  }

  return result;
}

export function getRule(key, rules) {
  return rules.find((x) => x.key === key);
}

export function evaluateRule(rule, data) {
  let evaluation = generateEvaluation(rule, data);
  try {
    return eval(evaluation);
  } catch (e) {
    return null;
  }
}

export function generateEvaluation(item, data) {
  switch (item.type) {
    case queryBuilderTypes.rule: {
      return generate(item.query);
    }
    case queryBuilderTypes.group: {
      return getEvaluations(item.query);
    }
    default: {
      return getEvaluations(item);
    }
  }

  function getEvaluations(target) {
    let evaluations = [];

    target.children.forEach((x) => {
      evaluations.push(generateEvaluation(x, data));
    });

    let separator = "";
    for (let key in queryBuildLogicalOperators) {
      if (key === target.logicalOperator)
        separator = queryBuildLogicalOperators[key];
    }

    return `(${evaluations.join(` ${separator} `)})`;
  }

  // TODO: Should be smarter about data type and operator usage.
  //       Example: (2 > 0) is better than ('2' > '0');
  function generate(query) {
    let keyVal = data.find((x) => x.key === query.operand);
    let val = keyVal ? keyVal.value : null;
    let operator = query.operator;
    if (operator === "=") operator = "==";
    return `('${val}' ${operator} '${query.value}')`;
  }
}

export function getQuestionHTML(val) {
  return val.replace(/\(.+\)\[.+\]/g, function(x) {
    let label = x.match(/\(.+\)/g);
    let description = x.match(/\[.+\]/g);

    label = label.length ? label[0].slice(1, -1) : null;
    description = description.length ? description[0].slice(1, -1) : null;

    if (label && description) {
      x = `
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <span class="font-weight-bold primary--text">${label}</span>
          </template>
          <span>${description}</span>
        </v-tooltip>
      </div>`;
    }

    return x;
  });
}

export const questionTextPartType = {
  text: "text",
  url: "url",
  tooltip: "tooltip",
};

let specialsRegex = /\(.+\)\[.+\]/g;
let labelRegex = /\(.+\)/g;
let valueRegex = /\[.+\]/g;

export function cleanQuestionText(val) {
  let result = val.split(specialsRegex);

  val.match(specialsRegex)?.forEach((x, i) => {
    let cleaned = "";

    let labelTest = x.match(labelRegex);
    if (labelTest && labelTest.length > 0) {
      cleaned = labelTest[0].slice(1, -1);
    }

    result.splice(i + 1, 0, cleaned);
  });

  return result;
}

export function getQuestionTextParts(val) {
  let result = val.split(specialsRegex).map((x) => ({
    type: "text",
    value: x,
  }));

  val.match(specialsRegex)?.forEach((x, i) => {
    let res = {};

    let labelTest = x.match(labelRegex);
    if (labelTest && labelTest.length > 0) {
      res.label = labelTest[0].slice(1, -1);
    }

    let valueTest = x.match(valueRegex);
    if (valueTest && valueTest.length > 0) {
      res.value = valueTest[0].slice(1, -1);
    }

    if (isValidHttpUrl(res.value)) {
      res.type = "url";
    } else if (res.value && res.value.length > 0) {
      res.type = "tooltip";
    } else {
      res.type = "text";
    }

    result.splice(i + 1, 0, res);
  });

  return result;

  function isValidHttpUrl(string) {
    let url;

    try {
      url = new URL(string);
    } catch (_) {
      return false;
    }

    return url.protocol === "http:" || url.protocol === "https:";
  }
}
